import { useTranslations } from '@/i18n/use-translations';
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Stack,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useEventStore } from '@/state/stores/use-event-store';
import { useNotifications } from '@/hooks/use-notifications';
import { useCreateTermMutation } from '@/state/mutations/events/use-create-term.ts';
import { usePublishTermMutation } from '@/state/mutations/events/use-publish-term.ts';
import { useGetTermsQuery, useGetPublishedVersionsQuery } from '@/state/queries/events/use-get-terms.ts';
import { TERM_TYPE, type TermType } from '@/types/dtos/events/term.dto.ts';
import { getObjectId } from '@/helpers/object.id.helper';
import { ArrowBack, ArrowForward, Publish } from '@mui/icons-material';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';

type ViewMode = 'edit' | 'published';

export default function LegalTermsForm() {
  const t = useTranslations();
  const notifications = useNotifications();
  const event = useEventStore((s) => s.event);
  const eventPlan = useEventStore((s) => s.eventPlan);

  const [viewMode, setViewMode] = useState<ViewMode>('edit');
  const [text, setText] = useState('');
  const [currentPublishedIndex, setCurrentPublishedIndex] = useState(0);
  const [initialText, setInitialText] = useState('');

  const eventId = eventPlan?.id || (event?.origin ? getObjectId(event.origin) : event?.id);
  const termType: TermType = TERM_TYPE.CONTRIBUTION_TERMS;

  // Load PLAN term
  const { data: planTermsData, isLoading: isLoadingPlan } = useGetTermsQuery(eventId, {
    mode: 'PLAN',
    termType,
    enabled: !!eventId,
  });

  // Load published versions (also used to compare versions)
  const { data: publishedVersionsData, isLoading: isLoadingPublished } = useGetPublishedVersionsQuery(
    eventId,
    termType,
    {
      enabled: !!eventId,
    },
  );

  const createTermMutation = useCreateTermMutation({
    onSuccess: () => {
      notifications.notifySuccess(t('event.detail.settingsMenu.legalTerms.notification.updateSuccess'));
      // Update initial text after save to reflect saved state
      setInitialText(text.trim());
    },
  });

  const publishTermMutation = usePublishTermMutation({
    onSuccess: () => {
      notifications.notifySuccess(t('event.detail.settingsMenu.legalTerms.notification.publishSuccess'));
      // Update initial text to reflect the published state
      setInitialText(text.trim());
      setViewMode('published');
    },
  });

  // Handle mutation errors
  useEffect(() => {
    if (createTermMutation.isError) {
      notifications.notifyError(t('event.detail.settingsMenu.legalTerms.notification.updateError'));
    }
  }, [createTermMutation.isError, notifications, t]);

  useEffect(() => {
    if (publishTermMutation.isError) {
      notifications.notifyError(t('event.detail.settingsMenu.legalTerms.notification.publishError'));
    }
  }, [publishTermMutation.isError, notifications, t]);

  // Initialize text from PLAN term and track initial state
  useEffect(() => {
    queueMicrotask(() => {
      if (planTermsData?.terms && planTermsData.terms.length > 0) {
        const latestTerm = planTermsData.terms.reduce((latest, term) =>
          term.version > latest.version ? term : latest,
        );
        setText(latestTerm.text);
        setInitialText(latestTerm.text);
      } else {
        setText('');
        setInitialText('');
      }
    });
  }, [planTermsData]);

  // Calculate if there are unsaved changes
  const hasUnsavedChanges = text.trim() !== initialText.trim();

  // Get latest PLAN version
  const latestPlanVersion =
    planTermsData?.terms && planTermsData.terms.length > 0
      ? Math.max(...planTermsData.terms.map((term) => term.version))
      : 0;

  // Get latest ORGANIZE version from published versions
  const latestOrganizeVersion =
    publishedVersionsData?.terms && publishedVersionsData.terms.length > 0
      ? Math.max(...publishedVersionsData.terms.map((term) => term.version))
      : 0;

  // PUBLISH should be enabled if:
  // - ORGANIZE version is lower than PLAN version, OR
  // - There are unsaved changes
  const canPublish = latestOrganizeVersion < latestPlanVersion || hasUnsavedChanges;

  const handleSave = async () => {
    if (!eventId) return;

    await createTermMutation.mutateAsync({
      eventId,
      data: {
        type: termType,
        text: text.trim(),
      },
    });
    // Update initial text after save
    setInitialText(text.trim());
  };

  const handlePublish = async () => {
    if (!eventId) return;

    try {
      // If there are unsaved changes, save to PLAN first
      if (hasUnsavedChanges) {
        await createTermMutation.mutateAsync({
          eventId,
          data: {
            type: termType,
            text: text.trim(),
          },
        });
        // Update initial text after save to reflect saved state
        setInitialText(text.trim());
      }

      // Then publish to ORGANIZE (this will use the latest PLAN version)
      await publishTermMutation.mutateAsync({
        eventId,
        termType,
      });
    } catch (error) {
      // Error handling is done in mutation callbacks
      console.error('Error publishing term:', error);
    }
  };

  const handleViewPublished = () => {
    setViewMode('published');
    // Sort by version ascending (oldest first, latest last) and default to latest (last index)
    const sortedVersions = [...(publishedVersionsData?.terms ?? [])].sort((a, b) => a.version - b.version);
    setCurrentPublishedIndex(sortedVersions.length > 0 ? sortedVersions.length - 1 : 0);
  };

  const handleBackToEdit = () => {
    setViewMode('edit');
  };

  // Sort published versions by version number ascending (oldest first, latest last)
  // This ensures the latest version appears on the right in the carousel
  const publishedVersions = publishedVersionsData?.terms
    ? [...publishedVersionsData.terms].sort((a, b) => a.version - b.version)
    : [];
  const currentPublishedVersion = publishedVersions[currentPublishedIndex];

  const handlePreviousVersion = () => {
    setCurrentPublishedIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextVersion = () => {
    setCurrentPublishedIndex((prev) => Math.min(publishedVersions.length - 1, prev + 1));
  };

  if (!eventId) {
    return <Alert severity="warning">{t('event.detail.settingsMenu.legalTerms.error.eventNotFound')}</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2, pr: 1 }}>
      <Typography variant="h6">{t('event.detail.settingsMenu.legalTerms.title')}</Typography>

      {viewMode === 'edit' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 2, minHeight: 0 }}>
          <Card variant="outlined" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, gap: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  'flexWrap': { xs: 'wrap', sm: 'nowrap' },
                  'gap': { xs: 1, sm: 0 },
                  '& > *': { maxWidth: '100%' },
                }}
              >
                <Box sx={{ minWidth: 0, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    {t('event.detail.settingsMenu.legalTerms.editMode')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {t('event.detail.settingsMenu.legalTerms.contributionTerms')}
                  </Typography>
                </Box>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{
                    'flexWrap': { xs: 'wrap', sm: 'nowrap' },
                    '& > *': { maxWidth: '100%' },
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleViewPublished}
                    disabled={publishedVersions.length === 0}
                  >
                    {t('event.detail.settingsMenu.legalTerms.versions')}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSave}
                    disabled={createTermMutation.isPending || !text.trim() || !hasUnsavedChanges}
                  >
                    {createTermMutation.isPending ? (
                      <>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Saving...
                      </>
                    ) : (
                      t('event.detail.settingsMenu.legalTerms.saveDraft')
                    )}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<Publish />}
                    onClick={handlePublish}
                    disabled={
                      publishTermMutation.isPending || createTermMutation.isPending || !text.trim() || !canPublish
                    }
                  >
                    {publishTermMutation.isPending || createTermMutation.isPending ? (
                      <>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Publishing...
                      </>
                    ) : (
                      t('event.detail.settingsMenu.legalTerms.publish')
                    )}
                  </Button>
                </Stack>
              </Stack>

              {isLoadingPlan ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TextField
                  multiline
                  fullWidth
                  minRows={10}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={t('event.detail.settingsMenu.legalTerms.textPlaceholder')}
                  sx={{
                    'flex': 1,
                    '& .MuiInputBase-root': {
                      height: '100%',
                      alignItems: 'flex-start',
                    },
                    '& .MuiInputBase-input': {
                      height: '100% !important',
                      overflow: 'auto !important',
                    },
                  }}
                />
              )}
            </CardContent>
          </Card>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 2, minHeight: 0 }}>
          <Card variant="outlined" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, gap: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  'flexWrap': { xs: 'wrap', sm: 'nowrap' },
                  'gap': { xs: 1, sm: 0 },
                  '& > *': { maxWidth: '100%' },
                }}
              >
                <Box sx={{ minWidth: 0, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    {t('event.detail.settingsMenu.legalTerms.publishedVersions')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {t('event.detail.settingsMenu.legalTerms.contributionTerms')}
                  </Typography>
                </Box>
                <Button variant="outlined" size="small" onClick={handleBackToEdit}>
                  {t('event.detail.settingsMenu.legalTerms.backToEdit')}
                </Button>
              </Stack>

              {isLoadingPublished ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                  <CircularProgress />
                </Box>
              ) : publishedVersions.length === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flex: 1,
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    {t('event.detail.settingsMenu.legalTerms.noPublishedVersions')}
                  </Typography>
                  <Button variant="outlined" size="small" onClick={handleBackToEdit}>
                    {t('event.detail.settingsMenu.legalTerms.backToEdit')}
                  </Button>
                </Box>
              ) : (
                <>
                  <Stack direction="row" alignItems="center" spacing={2} justifyContent="center">
                    <IconButton
                      onClick={handlePreviousVersion}
                      disabled={currentPublishedIndex === 0}
                      aria-label="Previous version"
                    >
                      <ArrowBack />
                    </IconButton>

                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('event.detail.settingsMenu.legalTerms.version')} {currentPublishedIndex + 1}/
                        {publishedVersions.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {currentPublishedVersion.publishedAt
                          ? format(new Date(currentPublishedVersion.publishedAt), 'PPpp')
                          : ''}
                      </Typography>
                    </Box>

                    <IconButton
                      onClick={handleNextVersion}
                      disabled={currentPublishedIndex === publishedVersions.length - 1}
                      aria-label="Next version"
                    >
                      <ArrowForward />
                    </IconButton>
                  </Stack>

                  <Box
                    sx={{
                      'flex': 1,
                      'overflow': 'auto',
                      'p': 2,
                      'border': '1px solid',
                      'borderColor': 'divider',
                      'borderRadius': 1,
                      'backgroundColor': 'background.default',
                      '& > *': {
                        'marginTop': 0,
                        'marginBottom': '1em',
                        '&:last-child': {
                          marginBottom: 0,
                        },
                      },
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        marginTop: '1em',
                        marginBottom: '0.5em',
                        fontWeight: 600,
                      },
                      '& ul, & ol': {
                        paddingLeft: '1.5em',
                        marginBottom: '1em',
                      },
                      '& li': {
                        marginBottom: '0.5em',
                      },
                      '& p': {
                        marginBottom: '1em',
                      },
                      '& strong': {
                        fontWeight: 600,
                      },
                      '& em': {
                        fontStyle: 'italic',
                      },
                    }}
                  >
                    <ReactMarkdown>{currentPublishedVersion.text}</ReactMarkdown>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
}
